import React, { useEffect, useMemo, useState } from 'react'
import { BILLING_DECIMAL_PLACES } from '../../constants/billingConstants';
import { connect } from 'react-redux';
import withBase from '../../hoc/withBase';

import * as actions from '../../store/actions/invoiceDownloads'

import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import Card from '../../components/Card/Card';
import CardHeader from '../../components/Card/CardHeader';
import CardBody from '../../components/Card/CardBody';

import Table from '../../components/Table/Table';

import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import Chip from '@material-ui/core/Chip'
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Button from '@material-ui/core/Button/';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import InfoIcon from '@material-ui/icons/Info';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import SearchIcon from '@material-ui/icons/Search';

import CustomInput from "../../components/CustomInput/CustomInput";

// Import centralized theme
import appTheme from '../../themes/theme';

const useStyles = makeStyles((theme) => ({
  fab: {
    position: 'fixed',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    zIndex: appTheme.zIndex.fixed,
    boxShadow: appTheme.shadows.elevated,
    transition: appTheme.transitions.button,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: appTheme.shadows.elevated,
    },
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxHeight: '90vh',
    width: '100vw',
  },
  paper: {
    backgroundColor: appTheme.colors.neutral.white,
    border: appTheme.components.card.border,
    borderRadius: appTheme.borderRadius.medium,
    boxShadow: appTheme.shadows.elevated,
    padding: appTheme.spacing.xl,
    width: 'min(760px, 92vw)',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  detailsModal: {
    backgroundColor: appTheme.colors.neutral.white,
    border: appTheme.components.card.border,
    borderRadius: appTheme.borderRadius.medium,
    boxShadow: appTheme.shadows.elevated,
    padding: 0,
    width: 'min(660px, 92vw)',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: appTheme.spacing.md,
    marginBottom: appTheme.spacing.md,
  },
  detailsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: appTheme.spacing.lg,
    background: `linear-gradient(135deg, ${appTheme.colors.primary.main} 0%, ${appTheme.colors.primary.dark} 100%)`,
    color: appTheme.colors.primary.contrast,
    borderTopLeftRadius: appTheme.borderRadius.medium,
    borderTopRightRadius: appTheme.borderRadius.medium,
  },
  detailsBody: {
    padding: appTheme.spacing.lg,
  },
  detailRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: `${appTheme.spacing.md} 0`,
    borderBottom: `1px solid ${appTheme.colors.neutral.border}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  detailLabel: {
    fontWeight: appTheme.typography.fontWeight.semibold,
    color: appTheme.colors.neutral.mediumText,
    fontSize: appTheme.typography.fontSize.bodySmall,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    minWidth: '140px',
  },
  detailValue: {
    color: appTheme.colors.neutral.darkText,
    fontSize: appTheme.typography.fontSize.bodyRegular,
    fontWeight: appTheme.typography.fontWeight.regular,
    textAlign: 'right',
    flex: 1,
  },
  statusChip: {
    padding: `${appTheme.spacing.xs} ${appTheme.spacing.md}`,
    borderRadius: appTheme.borderRadius.pill,
    fontSize: appTheme.typography.fontSize.caption,
    fontWeight: appTheme.typography.fontWeight.semibold,
    display: 'inline-flex',
    alignItems: 'center',
    gap: appTheme.spacing.xs,
  },
  operatorList: {
    border: `1px solid ${appTheme.colors.neutral.gray200}`,
    borderRadius: appTheme.borderRadius.small,
    maxHeight: 320,
    overflowY: 'auto',
    backgroundColor: appTheme.colors.neutral.white,
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: appTheme.spacing.sm,
    marginTop: appTheme.spacing.md,
  },
  hint: {
    background: appTheme.colors.status.info.light,
    border: `1px solid ${appTheme.colors.primary.main}`,
    borderRadius: appTheme.borderRadius.small,
    padding: appTheme.spacing.md,
    marginTop: appTheme.spacing.lg,
  },
  actionsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: appTheme.spacing.md,
    marginTop: appTheme.spacing.lg,
  }
}));

const BillingRuns = (props) => {
  const classes = useStyles();

  const [runs, setRuns] = useState([]);
  const [operatorsAndNames, setOperatorsAndNames] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [isProcessing, setProcessing] = useState(false);

  const [invoiceDate, setInvoiceDate] = useState(() => {
    const date = new Date();
    return date.getMonth()
      ? date.getFullYear() * 1e2 + date.getMonth()
      : (date.getFullYear() - 1) * 1e2 + 12;
  });

  const [selectAllOperator, setSelectAllOperator] = useState(true);
  const [selectedOperator, setSelectedOperator] = useState([]);
  const [operatorSearchQuery, setOperatorSearchQuery] = useState('');

  const refresh = () => {
    props.getSoracomInvoicesProcess((err, data) => {
      if (!err) setRuns(data);
    });
  }

  useEffect(() => {
    refresh();
    props.getAllOperatorsAndNames((err, data) => {
      if (!err) setOperatorsAndNames(data.data);
    })

    return () => {
      setRuns([])
      setOperatorsAndNames([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const headCells = useMemo(() => ([
    { id: 'id', label: '#', isSearchable: false },
    { id: 'period', label: 'Period', isSearchable: false },
    { id: 'scope', label: 'Scope', isSearchable: false },
    { id: 'status', label: 'Status', isSearchable: false },
    { id: 'downloaded', label: 'Downloaded', isSearchable: false },
    { id: 'errors', label: 'Errors', isSearchable: false },
    { id: 'drive', label: 'Drive', isSearchable: false },
    { id: 'total', label: 'Total (USD)', isSearchable: false },
    { id: 'triggeredBy', label: 'Type', isSearchable: false },
    { id: 'createdAt', label: 'Created', isSearchable: false, type: 'Date' }
  ]), []);

  const scopeLabel = (process) => {
    // Compatibility: current API doesn't return selected operator list.
    return process.triggeredBy === 'USER' ? 'ALL/SELECTED' : 'AUTO'
  }

  const formatInvoiceDate = (invoiceDate) => {
    // invoiceDate format: YYYYMM (e.g., 202405)
    if (!invoiceDate || invoiceDate.toString().length !== 6) return invoiceDate;
    const dateStr = invoiceDate.toString();
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = parseInt(month, 10) - 1;
    return monthNames[monthIndex] ? `${monthNames[monthIndex]} ${year}` : invoiceDate;
  }

  const handleInvoiceDateEvent = (event) => {
    const isInvoiceDateError = ('' + event.target.value).length !== 6
    if (!isInvoiceDateError) {
      const lastTwo = +('' + event.target.value).substr(4);
      const year = (+event.target.value - lastTwo);
      const reminder = lastTwo % 13;
      if (reminder) setInvoiceDate(year + reminder)
      else if (lastTwo === 13) setInvoiceDate(year + 100 + 1)
      else setInvoiceDate(year - 100 + 12)
    }
  }

  const handleToggle = (value) => () => {
    const currentIndex = selectedOperator.indexOf(value);
    const newChecked = [...selectedOperator];
    if (currentIndex === -1) newChecked.push(value);
    else newChecked.splice(currentIndex, 1);
    setSelectedOperator(newChecked);
  };

  const filteredOperators = useMemo(() => {
    if (!operatorSearchQuery.trim()) return operatorsAndNames;
    const query = operatorSearchQuery.toLowerCase();
    return operatorsAndNames.filter(op =>
      op.name.toLowerCase().includes(query) ||
      op.operatorId.toLowerCase().includes(query)
    );
  }, [operatorsAndNames, operatorSearchQuery]);

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    if (!selectAllOperator && !selectedOperator.length) {
      props.showNotification({ color: 'warning', message: "Please check your selections.", time: 3000 })
      return;
    }

    setProcessing(true)
    props.startSummaryDownload(
      invoiceDate,
      selectAllOperator ? [] : selectedOperator,
      (err, data) => {
        if (err) {
          props.showNotification({
            color: 'danger',
            message: err.response && err.response.data ? err.response.data.message : 'Please refresh!',
            time: 3000
          })
        } else {
          props.showNotification({ color: 'success', message: data.data.message, time: 3000 })
        }

        setProcessing(false);
        setModalOpen(false);
        refresh();
      }
    );
  }

  const handleRowClick = (event, row) => {
    const run = runs.find(r => r._id === row[0]);
    if (run) {
      setSelectedRun(run);
      setDetailsModalOpen(true);
    }
  }

  const getStatusColor = (status) => {
    if (['COMPLETED'].includes(status)) return appTheme.colors.status.success.main;
    if (['COMPLETED_WITH_ERRORS'].includes(status)) return appTheme.colors.status.warning.main;
    if (['FAILED'].includes(status)) return appTheme.colors.status.error.main;
    if (['PENDING', 'STARTED'].includes(status)) return appTheme.colors.primary.main;
    return appTheme.colors.neutral.gray400;
  }

  const getStatusIcon = (status) => {
    if (['COMPLETED'].includes(status)) return <CheckCircleIcon fontSize="small" />;
    if (['FAILED', 'COMPLETED_WITH_ERRORS'].includes(status)) return <ErrorIcon fontSize="small" />;
    return <InfoIcon fontSize="small" />;
  }

  const detailsModal = selectedRun && (
    <Modal
      className={classes.modal}
      open={detailsModalOpen}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      onClose={() => setDetailsModalOpen(false)}
    >
      <Fade in={detailsModalOpen}>
        <div className={classes.detailsModal}>
          <div className={classes.detailsHeader}>
            <div>
              <Typography variant="h6" style={{ fontWeight: appTheme.typography.fontWeight.bold, marginBottom: appTheme.spacing.xs }}>
                Billing Run Details
              </Typography>
              <Typography variant="body2" style={{ opacity: 0.9 }}>
                {formatInvoiceDate(selectedRun.invoiceDate)}
              </Typography>
            </div>
            <IconButton
              size="small"
              onClick={() => setDetailsModalOpen(false)}
              style={{ color: appTheme.colors.primary.contrast }}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <div className={classes.detailsBody}>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Run ID</Typography>
              <Typography className={classes.detailValue} style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                {selectedRun._id}
              </Typography>
            </div>

            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Period</Typography>
              <Typography className={classes.detailValue} style={{ fontWeight: appTheme.typography.fontWeight.semibold }}>
                {formatInvoiceDate(selectedRun.invoiceDate)}
              </Typography>
            </div>

            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Status</Typography>
              <div className={classes.detailValue}>
                <span
                  className={classes.statusChip}
                  style={{
                    backgroundColor: `${getStatusColor(selectedRun.downloadStatus)}15`,
                    color: getStatusColor(selectedRun.downloadStatus),
                  }}
                >
                  {getStatusIcon(selectedRun.downloadStatus)}
                  {selectedRun.downloadStatus}
                </span>
              </div>
            </div>

            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Scope</Typography>
              <Typography className={classes.detailValue}>
                {scopeLabel(selectedRun)}
              </Typography>
            </div>

            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Triggered By</Typography>
              <Typography className={classes.detailValue}>
                <Chip
                  size="small"
                  label={selectedRun.triggeredBy || 'AUTO'}
                  style={{
                    backgroundColor: selectedRun.triggeredBy === 'USER' ? appTheme.colors.primary.light : appTheme.colors.neutral.gray100,
                    color: selectedRun.triggeredBy === 'USER' ? appTheme.colors.primary.dark : appTheme.colors.neutral.darkText,
                  }}
                />
              </Typography>
            </div>

            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Downloaded</Typography>
              <Typography className={classes.detailValue} style={{ color: appTheme.colors.status.success.dark, fontWeight: appTheme.typography.fontWeight.semibold }}>
                <CheckCircleIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {(selectedRun.completedDownload || []).length} operators
              </Typography>
            </div>

            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Errors</Typography>
              <Typography className={classes.detailValue} style={{ color: selectedRun.errorDownload?.length ? appTheme.colors.status.error.dark : appTheme.colors.neutral.mediumText, fontWeight: appTheme.typography.fontWeight.semibold }}>
                {selectedRun.errorDownload?.length ? <ErrorIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} /> : null}
                {(selectedRun.errorDownload || []).length} operators
              </Typography>
            </div>

            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Drive Upload</Typography>
              <Typography className={classes.detailValue}>
                {selectedRun.driveUploadStatus || 'N/A'}
              </Typography>
            </div>

            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Total Amount</Typography>
              <Typography className={classes.detailValue} style={{ fontSize: appTheme.typography.fontSize.h6, fontWeight: appTheme.typography.fontWeight.bold, color: appTheme.colors.primary.main }}>
                $ {(selectedRun.total || 0).toFixed(BILLING_DECIMAL_PLACES)} USD
              </Typography>
            </div>

            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Created At</Typography>
              <Typography className={classes.detailValue}>
                {new Date(selectedRun.createdAt).toLocaleString()}
              </Typography>
            </div>

            <Box mt={3} display="flex" gap={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="default"
                onClick={() => setDetailsModalOpen(false)}
              >
                Close
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setDetailsModalOpen(false);
                  props.history.push(`/billing/billing-runs/${selectedRun._id}`);
                }}
                startIcon={<CloudDownloadIcon />}
              >
                View 3-Step Workflow
              </Button>
            </Box>
          </div>
        </div>
      </Fade>
    </Modal>
  );

  const startRunModal = (
    <Modal
      className={classes.modal}
      open={modalOpen}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      onClose={() => !isProcessing && setModalOpen(false)}
    >
      <Fade in={modalOpen}>
        <div className={classes.paper}>
          <form onSubmit={handleFormSubmit}>
            <div className={classes.modalHeader}>
              <div>
                <Typography variant="h6">New Billing Run</Typography>
                <Typography variant="body2" color="textSecondary">
                  Step 1 downloads raw billing (existing). Steps 2 & 3 are mocked in the run details screen.
                </Typography>
              </div>
              <Chip label="Step 1: Live" color="primary" size="small" />
            </div>

            <Divider style={{ marginBottom: 16 }} />

            <GridContainer>
              <GridItem xs={12} md={6}>
                <CustomInput
                  labelText="Period (YYYYMM) *"
                  inputProps={{ value: invoiceDate, onChange: handleInvoiceDateEvent, type: 'number' }}
                  formControlProps={{ fullWidth: true, className: classes.root }}
                />
              </GridItem>

              <GridItem xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectAllOperator}
                      onChange={(event) => setSelectAllOperator(event.target.checked)}
                      color="primary"
                    />
                  }
                  label={`All Operators (Total: ${operatorsAndNames.length})`}
                />
              </GridItem>

              {selectAllOperator ? null : (
                <GridItem xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Search operators by name or ID..."
                    value={operatorSearchQuery}
                    onChange={(e) => setOperatorSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: appTheme.colors.neutral.gray400 }} />
                        </InputAdornment>
                      ),
                    }}
                    style={{ marginBottom: appTheme.spacing.sm }}
                  />
                  <div className={classes.operatorList}>
                    <List dense>
                      {filteredOperators.length === 0 ? (
                        <ListItem>
                          <Typography variant="body2" color="textSecondary" style={{ padding: appTheme.spacing.md, textAlign: 'center', width: '100%' }}>
                            No operators found matching "{operatorSearchQuery}"
                          </Typography>
                        </ListItem>
                      ) : (
                        filteredOperators.map((value) => {
                          const labelId = `operator-id-${value.operatorId}`;
                          return (
                            <ListItem key={value.operatorId} role={undefined} button onClick={handleToggle(value.operatorId)}>
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  checked={selectedOperator.indexOf(value.operatorId) !== -1}
                                  tabIndex={-1}
                                  disableRipple
                                  inputProps={{ 'aria-labelledby': labelId }}
                                />
                              </ListItemIcon>
                              <ListItemText id={labelId} primary={`${value.name} [${value.operatorId}]`} />
                            </ListItem>
                          );
                        })
                      )}
                    </List>
                  </div>

                  {selectedOperator.length ? (
                    <div className={classes.chipsRow}>
                      <Typography variant="caption" color="textSecondary" style={{ width: '100%', marginBottom: appTheme.spacing.xs }}>
                        Selected operators ({selectedOperator.length}):
                      </Typography>
                      {selectedOperator.slice(0, 10).map(op => (
                        <Chip key={op} label={op} size="small" />
                      ))}
                      {selectedOperator.length > 10 ? (
                        <Chip label={`+${selectedOperator.length - 10} more`} size="small" variant="outlined" />
                      ) : null}
                    </div>
                  ) : null}
                </GridItem>
              )}
            </GridContainer>

            <div className={classes.actionsRow}>
              <Button variant='text' color='default' onClick={() => setModalOpen(false)} disabled={isProcessing}>
                Cancel
              </Button>
              <Button type="submit" color="primary" variant='contained' disabled={isProcessing}>
                Start Step 1
              </Button>
            </div>

            <div className={classes.hint}>
              <Typography variant="body2">
                Tip: For reruns, switch off “All Operators” and pick just the operator(s) you want to download.
              </Typography>
            </div>
          </form>
        </div>
      </Fade>
    </Modal>
  )

  return (
    <>
      {startRunModal}
      {detailsModal}

      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary">
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <div>
                  <Typography variant="h6" style={{ margin: 0, color: appTheme.colors.primary.contrast }}>
                    Billing Runs
                  </Typography>
                  <Typography variant="body2" style={{ margin: 0, color: 'rgba(255,255,255,0.85)' }}>
                    View all billing runs and click to see details
                  </Typography>
                </div>
              </Box>
            </CardHeader>
            <CardBody>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  All billing runs are listed below. Click on a run to see its 3-step workflow: Download → Process → Export.
                </Typography>
              </Box>
              <Table
                headCells={headCells}
                order="desc"
                orderBy="createdAt"
                rows={runs.map((p) => ([
                  p._id,
                  formatInvoiceDate(p.invoiceDate),
                  scopeLabel(p),
                  p.downloadStatus,
                  (p.completedDownload || []).length,
                  (p.errorDownload || []).length,
                  p.driveUploadStatus || 'N/A',
                  (p.total || 0).toFixed(BILLING_DECIMAL_PLACES),
                  p.triggeredBy || 'AUTO',
                  new Date(p.createdAt)
                ]))}
                name="Billing Runs"
                onClick={handleRowClick}
              />
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      <Fab color="primary" className={classes.fab} onClick={() => setModalOpen(true)}>
        <AddIcon />
      </Fab>
    </>
  )
}

export default withBase(connect(null, actions)(BillingRuns));
